import React from 'react';
import { Star, Quote, Building2, User, Briefcase, Store } from 'lucide-react';

const Testimonials = () => {
  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Freelance Consultant",
      company: "Mitchell Consulting",
      image: null,
      icon: User,
      rating: 5,
      text: "I used to pay my lawyer $350 just to review a single client contract. Now I upload it here, get a plain-English breakdown in seconds, and actually understand what I'm signing. Saved me over $2,000 last month alone.",
      savings: "$2,000+/month"
    },
    {
      name: "Marcus Chen",
      role: "Founder & CEO",
      company: "TechStart Solutions",
      image: null,
      icon: Briefcase,
      rating: 5,
      text: "As a startup founder, legal costs were killing our runway. This tool lets us handle NDAs, vendor contracts, and employee agreements ourselves. We've cut our legal spending by 80% while still staying protected.",
      savings: "80% reduction"
    },
    {
      name: "Jennifer Rodriguez",
      role: "Owner",
      company: "Bella's Boutique",
      image: null,
      icon: Store,
      rating: 5,
      text: "I'm a small retail shop owner, not a lawyer. When vendors send me contracts full of legal jargon, I was lost. Now I just upload the document and get it explained like I'm talking to a friend. Game changer!",
      savings: "Peace of mind"
    },
    {
      name: "David Thompson",
      role: "Real Estate Investor",
      company: "Thompson Properties LLC",
      image: null,
      icon: Building2,
      rating: 5,
      text: "I deal with purchase agreements, lease contracts, and partnership docs weekly. The AI analysis catches things I would have missed. Found a problematic liability clause last week that could have cost me $50K.",
      savings: "Avoided $50K loss"
    },
    {
      name: "Amanda Foster",
      role: "Marketing Agency Owner",
      company: "Foster Creative",
      image: null,
      icon: Briefcase,
      rating: 5,
      text: "Client contracts, freelancer agreements, vendor NDAs — we deal with a lot of paperwork. The batch processing feature is incredible. What took my assistant a full day now takes 20 minutes.",
      savings: "90% time saved"
    },
    {
      name: "Robert Kim",
      role: "E-commerce Entrepreneur",
      company: "KimGoods Online",
      image: null,
      icon: Store,
      rating: 5,
      text: "Supplier contracts from overseas were always intimidating. The AI breaks down every clause and flags anything unusual. I finally feel confident negotiating terms because I actually understand them.",
      savings: "Confident negotiations"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Trusted by 50,000+ Small Business Owners
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Real business owners sharing how they've saved thousands on legal costs 
            while staying protected.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            const IconComponent = testimonial.icon;
            return (
              <div 
                key={index}
                className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all duration-300"
              >
                {/* Quote Icon */}
                <Quote className="h-8 w-8 text-blue-200 mb-4" />
                
                {/* Rating */}
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Savings Badge */}
                <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full mb-4">
                  Result: {testimonial.savings}
                </div>

                {/* Author */}
                <div className="flex items-center pt-4 border-t border-slate-200">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mr-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                    <div className="text-sm text-blue-600">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-slate-400">
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900">50,000+</div>
              <div className="text-sm">Business Owners</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900">$12M+</div>
              <div className="text-sm">Legal Costs Saved</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900">500,000+</div>
              <div className="text-sm">Documents Processed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900">4.9/5</div>
              <div className="text-sm">Average Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
